/**
 * Sector terminology — single source of truth for words that change per
 * business sector (Hastalar vs Müşteriler vs Sakinler vs Üyeler vs ...).
 *
 * This file is the PURE-FUNCTION part (no React deps) so it can be unit
 * tested with Jest without bringing in AuthContext. The React hook lives
 * in `useSectorTerms.js` next to this file.
 *
 * USAGE in React components:
 *   import { useSectorTerms } from '@/utils/useSectorTerms';
 *   const terms = useSectorTerms();
 *   <h1>{terms.customerLabelPlural}</h1>     // "Hastalar" / "Müşteriler" / ...
 *
 * USAGE in pure helpers / tests:
 *   import { getSectorTerms } from '@/utils/sectorTerminology';
 *   const terms = getSectorTerms({ business_type: 'apartment' });
 *
 * DO NOT add NEW hardcoded "Hasta"/"Müşteri"/"Üye"/"Sakin" strings to the
 * codebase. Add the term here and consume it via the hook.
 */
import {
  MASTER_BUSINESS_TYPES,
  resolveMasterKey,
  getMasterTypeByKey,
} from '../lib/businessTypes';

/** Safe default — used when business_type is missing or invalid (e.g. before
 *  onboarding completes). */
export const DEFAULT_TERMS = {
  customerLabelPlural: 'Müşteriler',
  customerLabelSingular: 'Müşteri',
  addCustomerLabel: 'Müşteri Ekle',
  customerNoLabel: 'Müşteri No',
  appointmentLabel: 'Randevular',
  serviceLabel: 'Hizmet',
};

/**
 * Pure function — given a business descriptor, return the term set.
 * Use this when AuthContext is not available (e.g. inside server-side helpers,
 * tests, or storybook). For React components prefer `useSectorTerms()`.
 */
export const getSectorTerms = ({ business_type, appointment_category, appointment_subcategory } = {}) => {
  const key = resolveMasterKey({ business_type, appointment_category, appointment_subcategory });
  const master = key ? getMasterTypeByKey(key) : null;
  return { ...DEFAULT_TERMS, ...(master?.terms || {}) };
};

/** Convenience: iterate all sectors (for tests / sector-picker screens). */
export const ALL_SECTOR_TERMS = MASTER_BUSINESS_TYPES.map((m) => ({
  key: m.key,
  label: m.label,
  ...m.terms,
}));
